import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabaseClient'
import toast from 'react-hot-toast'
import {
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineSearch,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from 'react-icons/hi'
import ConfirmModal from '../../components/ConfirmModal'
import useDocumentTitle from '../../hooks/useDocumentTitle'

const PAGE_SIZE = 10

export default function AgencyRequestsPage() {
  useDocumentTitle('Agency Requests')

  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  
  // Single modal state for both approve and reject to keep it simple, or separate them by variant
  const [confirmModal, setConfirmModal] = useState({ open: false })

  const fetchRequests = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('agency_requests')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (search.trim()) {
        query = query.ilike('agency_name', `%${search.trim()}%`)
      }

      const { data, error, count } = await query

      if (error) throw error
      setRequests(data || [])
      setTotalCount(count || 0)
    } catch (err) {
      toast.error('Failed to load agency requests')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    fetchRequests()
  }, [fetchRequests])

  useEffect(() => {
    setPage(0)
  }, [search])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const handleApprove = (request) => {
    setConfirmModal({
      open: true,
      title: 'Approve Agency Request',
      message: `Are you sure you want to approve "${request.agency_name}" and create a new agency? This will generate a 6-digit access code and send an API request to finalize it.`,
      variant: 'primary',
      onConfirm: async () => {
        try {
          const access_code = Math.floor(100000 + Math.random() * 900000).toString()
          
          // 1. Create agency
          const { data: insertedData, error: insertError } = await supabase
            .from('agencies')
            .insert([{ 
              name: request.agency_name, 
              access_code 
            }])
            .select()

          if (insertError) throw insertError

          const newAgency = insertedData[0]

          // 1.5 Create profile with email and sector
          if (request.agency_email) {
            const { error: profileError } = await supabase
              .from('profiles')
              .insert([{
                email: request.agency_email,
                agency_id: newAgency.id,
                role_id: 'admin',
                sector_id: request.sector_id
              }])
            
            if (profileError) {
              console.error('Failed to create profile for agency:', profileError)
            }
          }

          // 2. Update the request status
          const { error: updateError } = await supabase
            .from('agency_requests')
            .update({ status: 'approved' })
            .eq('id', request.id)

          if (updateError) throw updateError

          toast.success('Agency created successfully')
          fetchRequests()
        } catch (err) {
          console.error(err)
          toast.error(err.message || 'Failed to approve request')
        }
        setConfirmModal({ open: false })
      },
    })
  }

  const handleReject = (request) => {
    setConfirmModal({
      open: true,
      title: 'Reject Agency Request',
      message: `Are you sure you want to reject the request for "${request.agency_name}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('agency_requests')
            .update({ status: 'rejected' })
            .eq('id', request.id)

          if (error) throw error
          toast.success('Agency request rejected')
          fetchRequests()
        } catch (err) {
          console.error(err)
          toast.error('Failed to reject request')
        }
        setConfirmModal({ open: false })
      },
    })
  }

  return (
    <div className="agencies-page">
      <div className="page-header">
        <div>
          <h1>Agency Requests</h1>
          <p>Review and approve incoming agency creation requests</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by agency name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="search-count">
          {totalCount} request{totalCount === 1 ? '' : 's'}
        </span>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <div className="loading-spinner" />
            <p>Loading requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="table-empty">
            <HiOutlineSearch style={{ fontSize: '2rem', opacity: 0.4 }} />
            <p>No agency requests found</p>
            {search && (
              <button
                className="btn btn-ghost"
                onClick={() => setSearch('')}
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Agency Name</th>
                <th>Email</th>
                <th>Sector</th>
                <th>Requested On</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td data-label="Agency">
                    <div className="cell-primary">
                      <strong>{request.agency_name}</strong>
                    </div>
                  </td>
                  <td data-label="Email">{request.agency_email || '-'}</td>
                  <td data-label="Sector">{request.sector_id || '-'}</td>
                  <td data-label="Requested">
                    {new Date(request.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td data-label="Status">
                    <span className={`badge ${request.status === 'approved' ? 'badge-success' : request.status === 'rejected' ? 'badge-danger' : 'badge-warning'}`}>
                      {request.status}
                    </span>
                  </td>
                  <td data-label="" className="td-actions">
                    <div className="table-actions">
                      {request.status === 'pending' && (
                        <>
                          <button
                            className="btn btn-icon"
                            style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.1)' }}
                            title="Approve"
                            onClick={() => handleApprove(request)}
                          >
                            <HiOutlineCheck />
                          </button>
                          <button
                            className="btn btn-icon btn-ghost-danger"
                            title="Reject"
                            onClick={() => handleReject(request)}
                          >
                            <HiOutlineX />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-ghost"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <HiOutlineChevronLeft /> Previous
          </button>
          <span className="pagination-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn-ghost"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <HiOutlineChevronRight />
          </button>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false })}
      />
    </div>
  )
}
